/**
 * AvcAdminPanel — Panneau d'administration du recensement AVC.
 * Extrait du monolithe App.js. Aucune règle métier n'a été modifiée.
 */
export const AvcAdminPanel = ({
  adminAvcCases, avcCount,
  fetchAdminAvcCases, deleteAvcCase,
}) => {
  const exportCSV = () => {
    if (!adminAvcCases.length) { alert("Aucun cas à exporter"); return; }
    const headers = ["nom_usage", "village", "age_approx", "statut", "contact_famille", "signale_par", "consentement_famille", "notes", "created_at"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [headers.join(",")].concat(
      adminAvcCases.map(c => headers.map(h => escape(c[h])).join(","))
    );
    const csv = rows.join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const dt = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `safuaye_avc_recensement_${dt}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reportUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/?report=1#urgence-avc`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=8&data=${encodeURIComponent(reportUrl)}`;

  const printQR = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>SAFUAYE — QR Recensement AVC</title><style>body{font-family:sans-serif;text-align:center;padding:30px;} h1{color:#991b1b;margin-bottom:6px;} p{color:#374151;margin:6px 0;} .box{border:3px solid #dc2626;border-radius:16px;padding:24px;display:inline-block;max-width:420px;} img{width:340px;height:340px;} .foot{font-size:12px;color:#6b7280;margin-top:12px;}</style></head><body onload="window.print()"><div class="box"><h1>SAFUAYE — Urgence AVC</h1><p><strong>Anciens joueurs d'Ifodjè FC</strong></p><p>Signalez un cas en scannant ce QR</p><img src="${qrSrc}" alt="QR"/><p class="foot">Ou visitez : safuaye.org — section « Urgence AVC »</p></div></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="admin-avc-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
          🩺 Recensement AVC — anciens joueurs d&apos;Ifodjè FC
        </h3>
        <div className="flex gap-2">
          <button
            onClick={fetchAdminAvcCases}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold"
            data-testid="admin-avc-refresh"
          >
            🔄 Rafraîchir
          </button>
          <button
            onClick={exportCSV}
            className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded font-semibold"
            data-testid="admin-avc-export-csv"
          >
            ⬇️ Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3">
          <p className="text-xs text-slate-600 uppercase font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-900">{avcCount.total}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-red-700 uppercase font-semibold">Hospitalisés</p>
          <p className="text-2xl font-bold text-red-900">{avcCount.hospitalises}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-3">
          <p className="text-xs text-orange-700 uppercase font-semibold">Survivants</p>
          <p className="text-2xl font-bold text-orange-900">{avcCount.survivants}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-600 uppercase font-semibold">Décédés</p>
          <p className="text-2xl font-bold text-gray-800">{avcCount.decedes}</p>
        </div>
      </div>

      {adminAvcCases.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <p className="text-gray-500 text-sm">Aucun cas signalé pour l&apos;instant.</p>
          <p className="text-gray-400 text-xs mt-1">Cliquez sur « 🔄 Rafraîchir » ou attendez les premiers signalements via le formulaire public.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
          <table className="w-full text-sm" data-testid="admin-avc-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Nom d&apos;usage</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Village</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Âge</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Statut</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Contact famille</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Signalé par</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Consent.</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Date</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {adminAvcCases.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{c.nom_usage}</td>
                  <td className="px-3 py-2 text-gray-600">{c.village || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{c.age_approx || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      c.statut === "hospitalise" ? "bg-red-100 text-red-800" :
                      c.statut === "survivant" ? "bg-orange-100 text-orange-800" :
                      "bg-gray-200 text-gray-800"
                    }`}>
                      {c.statut === "hospitalise" ? "Hospitalisé" : c.statut === "survivant" ? "Survivant" : "Décédé"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{c.contact_famille || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{c.signale_par || "—"}</td>
                  <td className="px-3 py-2 text-center">{c.consentement_famille ? "✅" : "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : "—"}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => deleteAvcCase(c.id)}
                      className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded font-semibold"
                      data-testid={`admin-avc-delete-${c.id}`}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded text-xs text-amber-900">
        🔒 <strong>Confidentialité :</strong> ces données sont strictement internes au Bureau National. Ne les partagez qu&apos;avec des partenaires institutionnels (CHR Atakpamé, OMS, bailleurs) après validation du CA. Le compteur public affiche uniquement les totaux anonymisés.
      </div>

      <div className="mt-6 bg-white rounded-xl shadow border border-gray-200 p-5" data-testid="admin-avc-qr">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">📱 QR code — Formulaire terrain</h4>
            <p className="text-xs text-gray-600 mt-1 max-w-md">À imprimer et afficher au siège d&apos;Atchrimi-Komé, dans les villages, ou à remettre aux Membres d&apos;Honneur lors des visites aux anciens joueurs.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <div className="flex flex-col items-center">
            <img src={qrSrc} alt="QR code recensement AVC" className="w-56 h-56 border-2 border-gray-200 rounded-lg bg-white p-2" data-testid="admin-avc-qr-image" />
            <div className="mt-3 flex gap-2">
              <a
                href={qrSrc}
                download="safuaye_avc_qr.png"
                target="_blank"
                rel="noreferrer"
                className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded font-semibold"
                data-testid="admin-avc-qr-download"
              >⬇️ Télécharger PNG</a>
              <button
                onClick={printQR}
                className="text-xs bg-gray-800 hover:bg-gray-900 text-white px-3 py-2 rounded font-semibold"
                data-testid="admin-avc-qr-print"
              >🖨️ Imprimer</button>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">URL cible du QR :</p>
            <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs text-gray-800 break-all font-mono mb-3">{reportUrl}</div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Ce QR ouvre directement <strong>le formulaire de signalement</strong> sur le site public. La personne qui scanne n&apos;a rien à chercher — le formulaire s&apos;affiche automatiquement.
            </p>
            <div className="mt-3 bg-emerald-50 border-l-4 border-emerald-500 p-2 rounded text-xs text-emerald-900">
              💡 <strong>Astuce impression :</strong> le bouton « Imprimer » ouvre une version A4 prête à afficher (encadré rouge + logo + URL de secours). Idéal pour la porte du siège ou les tableaux de village.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvcAdminPanel;
